import { useCallback } from "preact/hooks";
import { route } from "preact-router";
import { useData } from "../hooks/useData";
import { useTimeRange, type TimeRange } from "../hooks/useTimeRange";
import {
  fetchStatus,
  fetchInterfaceHistory,
  fetchHardware,
  type HardwareDevice,
} from "../utils/api";
import {
  formatBps,
  formatMs,
  formatTimeAgo,
  formatTimestamp,
} from "../utils/format";
import { SummaryCard } from "../components/SummaryCard";
import { StatusBadge } from "../components/StatusBadge";
import { TimeRangePicker } from "../components/TimeRangePicker";
import { SkeletonLoader } from "../components/SkeletonLoader";
import { ErrorMessage } from "../components/ErrorMessage";
import { LastUpdated } from "../components/LastUpdated";
import { InterfaceChart } from "../components/charts/InterfaceChart";
import "./Overview.css";

export default function Overview() {
  const { range, setRange } = useTimeRange();

  const fetchStatusCb = useCallback(() => fetchStatus(), []);
  const {
    data: status,
    loading: statusLoading,
    error: statusError,
    refresh: refreshStatus,
    lastUpdated: statusLastUpdated,
  } = useData(fetchStatusCb);

  const fetchHardwareCb = useCallback(() => fetchHardware(), []);
  const { data: hardware, loading: hardwareLoading } = useData(fetchHardwareCb);

  const fetchHistoryCb = useCallback(
    () => fetchInterfaceHistory(range),
    [range]
  );
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
    refresh: refreshHistory,
  } = useData(fetchHistoryCb);

  const handleRangeChange = (r: string) => setRange(r as TimeRange);

  const netwatch = status?.netwatch ?? [];
  const queues = status?.queues ?? [];
  const uplink = status?.uplink;
  const speedtest = status?.speedtest;

  const getDevice = (name: string): HardwareDevice | undefined =>
    hardware?.devices.find((d) => d.name === name);
  const rbDevice = getDevice("RB750Gr3");
  const lhgDevice = getDevice("LHG");

  const clientsUp = netwatch.filter((n) => n.status === "up").length;
  const clientsDown = netwatch.length - clientsUp;

  const sortedClients = [...netwatch].sort((a, b) => {
    if (a.status === "down" && b.status !== "down") return -1;
    if (a.status !== "down" && b.status === "down") return 1;
    return a.name.localeCompare(b.name);
  });

  const hasAlerts =
    uplink?.status === "down" || netwatch.some((n) => n.status === "down");

  const lastHistoryTimestamp =
    history && history.data.length > 0
      ? history.data[history.data.length - 1].timestamp
      : null;

  return (
    <div class="overview">
      <h1 class="overview__title">Overview</h1>

      {statusError && (
        <ErrorMessage message={statusError.message} onRetry={refreshStatus} />
      )}

      <section class="overview__cards">
        {statusLoading ? (
          <>
            <SkeletonLoader type="card" />
            <SkeletonLoader type="card" />
            <SkeletonLoader type="card" />
            <SkeletonLoader type="card" />
          </>
        ) : (
          <>
            <SummaryCard
              title="Status Uplink"
              value={uplink?.status === "up" ? "UP" : uplink?.status === "down" ? "DOWN" : "UNKNOWN"}
              subtitle={
                uplink
                  ? `${uplink.interface_name} | RX: ${formatBps(uplink.rx_rate)} | TX: ${formatBps(uplink.tx_rate)}`
                  : "Belum ada data uplink"
              }
              icon="🌐"
              variant={uplink?.status === "up" ? "success" : uplink?.status === "down" ? "danger" : "default"}
            />
            <SummaryCard
              title="Client Online"
              value={`${clientsUp} / ${netwatch.length}`}
              subtitle={`${clientsDown} client offline`}
              icon="👥"
              variant={clientsDown === 0 ? "success" : "warning"}
            />
            <SummaryCard
              title="Total Bandwidth"
              value={`↓ ${formatBps(status?.summary.total_download ?? 0)}`}
              subtitle={`↑ ${formatBps(status?.summary.total_upload ?? 0)}`}
              icon="📊"
            />
            <SummaryCard
              title="Last Speedtest"
              value={
                speedtest?.ookla
                  ? formatBps(speedtest.ookla.download)
                  : "Belum ada"
              }
              subtitle={
                speedtest?.ookla
                  ? `Ping: ${formatMs(speedtest.ookla.ping)} | ${formatTimeAgo(
                      speedtest.ookla.recorded_at
                    )}`
                  : "Speedtest Ookla terakhir"
              }
              icon="🚀"
            />
          </>
        )}
      </section>

      <section class="overview__cards overview__cards--hardware">
        {hardwareLoading ? (
          <>
            <SkeletonLoader type="card" />
            <SkeletonLoader type="card" />
          </>
        ) : (
          <>
            <SummaryCard
              title="Router RB750Gr3"
              value={
                rbDevice?.connection_status === "up"
                  ? "UP"
                  : rbDevice?.connection_status === "down"
                  ? "DOWN"
                  : "UNKNOWN"
              }
              subtitle={
                rbDevice
                  ? `CPU: ${rbDevice.cpu_load}% | RAM: ${rbDevice.memory_percent}%`
                  : "Belum ada data"
              }
              icon="🖥️"
              variant={
                rbDevice?.connection_status === "up"
                  ? "success"
                  : rbDevice?.connection_status === "down"
                  ? "danger"
                  : "default"
              }
            />
            <SummaryCard
              title="Wireless LHG"
              value={
                lhgDevice?.connection_status === "up"
                  ? "UP"
                  : lhgDevice?.connection_status === "down"
                  ? "DOWN"
                  : "UNKNOWN"
              }
              subtitle={
                lhgDevice
                  ? `CPU: ${lhgDevice.cpu_load}% | Signal: ${lhgDevice.wireless?.rx_signal ?? 0} dBm`
                  : "Belum ada data"
              }
              icon="📡"
              variant={
                lhgDevice?.connection_status === "up"
                  ? "success"
                  : lhgDevice?.connection_status === "down"
                  ? "danger"
                  : "default"
              }
            />
          </>
        )}
      </section>

      {hasAlerts && !statusLoading && (
        <section class="overview__alerts">
          <h2 class="overview__section-title">🚨 Alert</h2>
          <ul class="overview__alert-list">
            {uplink?.status === "down" && (
              <li class="overview__alert overview__alert--danger">
                Uplink {uplink.interface_name} DOWN
              </li>
            )}
            {netwatch
              .filter((n) => n.status === "down")
              .map((n) => (
                <li key={n.host} class="overview__alert overview__alert--danger">
                  Client <strong>{n.name}</strong> OFFLINE sejak{" "}
                  {formatTimeAgo(n.since ?? n.last_update)}
                </li>
              ))}
          </ul>
        </section>
      )}

      <section class="overview__section card">
        <div class="overview__section-header">
          <h2 class="overview__section-title">Traffic Uplink (ether1)</h2>
          <div class="overview__section-actions">
            <LastUpdated timestamp={lastHistoryTimestamp} />
            <TimeRangePicker value={range} onChange={handleRangeChange} />
          </div>
        </div>
        {historyError && (
          <ErrorMessage message={historyError.message} onRetry={refreshHistory} />
        )}
        <InterfaceChart
          data={history?.data ?? []}
          loading={historyLoading}
          error={historyError?.message}
        />
      </section>

      <section class="overview__section card">
        <h2 class="overview__section-title">Status Client</h2>
        {statusLoading ? (
          <SkeletonLoader type="table" rows={5} />
        ) : sortedClients.length === 0 ? (
          <p class="overview__empty">Belum ada data client</p>
        ) : (
          <div class="overview__table-wrapper">
            <table class="overview__table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Nama</th>
                  <th>IP</th>
                  <th>Download</th>
                  <th>Upload</th>
                  <th>Sejak</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((n) => {
                  const queue = queues.find((q) => q.name === n.name);
                  const isDown = n.status === "down";
                  return (
                    <tr
                      key={n.host}
                      class={`overview__table-row ${
                        isDown ? "overview__table-row--offline" : ""
                      }`}
                      onClick={() =>
                        route(`/clients?name=${encodeURIComponent(n.name)}`)
                      }
                      tabIndex={0}
                      role="button"
                      aria-label={`Lihat detail ${n.name}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          route(`/clients?name=${encodeURIComponent(n.name)}`);
                        }
                      }}
                    >
                      <td>
                        <StatusBadge
                          status={
                            n.status === "up" || n.status === "down"
                              ? n.status
                              : "unknown"
                          }
                        />
                      </td>
                      <td>{n.name}</td>
                      <td>{queue?.target ?? n.host}</td>
                      <td>{formatBps(queue?.rate_download ?? 0)}</td>
                      <td>{formatBps(queue?.rate_upload ?? 0)}</td>
                      <td>{formatTimeAgo(n.last_update)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section class="overview__section card">
        <h2 class="overview__section-title">Ringkasan Speedtest</h2>
        <div class="overview__speedtest-grid">
          <div class="overview__speedtest-card">
            <h3 class="overview__speedtest-source">MikroTik</h3>
            {speedtest?.mikrotik ? (
              <>
                <p class="overview__speedtest-value">
                  ↓ {formatBps(speedtest.mikrotik.download)}
                </p>
                <p class="overview__speedtest-detail">
                  ↑ {formatBps(speedtest.mikrotik.upload)} · {" "}
                  {formatMs(speedtest.mikrotik.ping)}
                </p>
                <p class="overview__speedtest-meta">
                  {speedtest.mikrotik.server_name}
                </p>
                <p class="overview__speedtest-meta">
                  {formatTimestamp(speedtest.mikrotik.recorded_at)}
                </p>
              </>
            ) : (
              <p class="overview__empty">Belum ada data speedtest</p>
            )}
          </div>
          <div class="overview__speedtest-card">
            <h3 class="overview__speedtest-source">Ookla</h3>
            {speedtest?.ookla ? (
              <>
                <p class="overview__speedtest-value">
                  ↓ {formatBps(speedtest.ookla.download)}
                </p>
                <p class="overview__speedtest-detail">
                  ↑ {formatBps(speedtest.ookla.upload)} · {" "}
                  {formatMs(speedtest.ookla.ping)}
                </p>
                <p class="overview__speedtest-meta">
                  {speedtest.ookla.server_name}
                </p>
                <p class="overview__speedtest-meta">
                  {formatTimestamp(speedtest.ookla.recorded_at)}
                </p>
              </>
            ) : (
              <p class="overview__empty">Belum ada data speedtest</p>
            )}
          </div>
        </div>
        <a href="/speedtest" class="overview__link">
          Lihat Detail →
        </a>
      </section>

      <footer class="overview__footer">
        <LastUpdated timestamp={statusLastUpdated?.toISOString() ?? null} />
      </footer>
    </div>
  );
}
